import { supabase } from '@/integrations/supabase/client';

export type ActivityType = 
  | 'login'
  | 'page_view'
  | 'feature_use'
  | 'project_create'
  | 'project_open'
  | 'chapter_create'
  | 'section_edit'
  | 'research_add'
  | 'pipeline_start'
  | 'pipeline_complete'
  | 'export';

export type FeatureName =
  | 'ai_assistant'
  | 'research_chat'
  | 'focus_writing'
  | 'writing_pipeline'
  | 'ambient_sounds'
  | 'web_research'
  | 'citations_manager'
  | 'export_pdf'
  | 'export_markdown';

export async function trackActivity(
  activityType: ActivityType,
  featureName?: FeatureName | string,
  metadata?: Record<string, unknown>
): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Use type assertion for the new table not yet in generated types
    await (supabase.from('user_activity_logs') as any).insert({
      user_id: user.id,
      activity_type: activityType,
      feature_name: featureName || null,
      metadata: metadata || {},
    });
  } catch (error) {
    // Silently fail - activity tracking should not block user actions
    console.debug('Activity tracking failed:', error);
  }
}

// Convenience functions for common activities
export const trackPageView = (pageName: string) => 
  trackActivity('page_view', undefined, { page: pageName });

export const trackFeatureUse = (featureName: FeatureName) => 
  trackActivity('feature_use', featureName);

export const trackLogin = () => 
  trackActivity('login');

export const trackProjectCreate = (projectId: string) => 
  trackActivity('project_create', undefined, { project_id: projectId });

export const trackProjectOpen = (projectId: string) => 
  trackActivity('project_open', undefined, { project_id: projectId });

export const trackPipelineStart = (projectId: string, mode: string) => 
  trackActivity('pipeline_start', 'writing_pipeline', { project_id: projectId, mode });

export const trackPipelineComplete = (projectId: string, mode: string) => 
  trackActivity('pipeline_complete', 'writing_pipeline', { project_id: projectId, mode });

export const trackExport = (format: 'pdf' | 'markdown', type: 'research' | 'manuscript') => 
  trackActivity('export', format === 'pdf' ? 'export_pdf' : 'export_markdown', { type });
